// Click-to-edit: the editor opens with the cursor where the note was clicked
// (staging check 03/11). The reading view and the editor render the same
// markdown, so the clicked block is found again by its text, and the cursor
// by its position in that text (whitespace collapsed). When the block cannot
// be found the editor opens as before.

export interface CaretTarget {
  // The clicked block's text (a paragraph, list item or heading).
  text: string;
  // Characters before the click in that text, whitespace collapsed.
  offset: number;
}

const BLOCKS = "p, li, h1, h2, h3, h4, h5, h6";

const collapse = (value: string): string => value.replace(/\s+/g, " ").trim();
// The reading view writes list markers as text ("• Continue …"); the editor
// has list items. The marker is left out of the comparison.
const MARKER = /^(?:[•·\-*]|\d+[.)])\s+/;
const marker = (text: string): number => MARKER.exec(text)?.[0].length ?? 0;

// Length of `value` once whitespace is collapsed and leading space dropped
// (trailing space counts: it sits before the cursor).
const collapsedLength = (value: string): number =>
  value.replace(/\s+/g, " ").replace(/^ /, "").length;

// The block around a caret position inside `root`, and the caret's offset
// in it.
export const caretTargetFromPosition = (
  root: HTMLElement,
  node: Node,
  offset: number,
): CaretTarget | null => {
  if (!root.contains(node)) return null;
  const element = node instanceof Element ? node : node.parentElement;
  const block = element?.closest(BLOCKS);
  if (!block || !root.contains(block)) return null;
  const full = collapse(block.textContent ?? "");
  const skip = marker(full);
  const text = full.slice(skip);
  if (!text) return null;
  const range = root.ownerDocument.createRange();
  range.setStart(block, 0);
  range.setEnd(node, offset);
  return {
    text,
    offset: Math.max(
      0,
      Math.min(collapsedLength(range.toString()) - skip, text.length),
    ),
  };
};

type CaretDocument = Document & {
  caretPositionFromPoint?: (
    x: number,
    y: number,
  ) => { offsetNode: Node; offset: number } | null;
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
};

// The block and offset under a click, or null (no browser support, or not
// on text).
export const caretTargetAt = (
  root: HTMLElement,
  x: number,
  y: number,
): CaretTarget | null => {
  const doc = root.ownerDocument as CaretDocument;
  try {
    if (typeof doc.caretPositionFromPoint === "function") {
      const position = doc.caretPositionFromPoint(x, y);
      return position
        ? caretTargetFromPosition(root, position.offsetNode, position.offset)
        : null;
    }
    if (typeof doc.caretRangeFromPoint === "function") {
      const range = doc.caretRangeFromPoint(x, y);
      return range
        ? caretTargetFromPosition(root, range.startContainer, range.startOffset)
        : null;
    }
  } catch {
    return null;
  }
  return null;
};

// Puts the cursor in `root` (the editor) at the target: the first block with
// the same text, at the same offset. False when there is no such block.
export const placeCaret = (root: HTMLElement, target: CaretTarget): boolean => {
  let skip = 0;
  const block = [...root.querySelectorAll<HTMLElement>(BLOCKS)].find(
    (candidate) => {
      const full = collapse(candidate.textContent ?? "");
      skip = marker(full);
      return full.slice(skip) === target.text;
    },
  );
  if (!block) return false;
  const wanted = target.offset + skip;
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let count = 0;
  let started = false;
  let lastSpace = false;
  let at: { node: Text; offset: number } | null = null;
  let last: Text | null = null;
  for (let node = walker.nextNode(); node && !at; node = walker.nextNode()) {
    const text = node as Text;
    last = text;
    for (let i = 0; i < text.data.length; i += 1) {
      if (count >= wanted) {
        at = { node: text, offset: i };
        break;
      }
      const space = /\s/.test(text.data[i] ?? "");
      if (space) {
        if (started && !lastSpace) count += 1;
      } else {
        started = true;
        count += 1;
      }
      lastSpace = space;
    }
  }
  if (!at && last) at = { node: last, offset: last.data.length };
  const range = doc.createRange();
  if (at) range.setStart(at.node, at.offset);
  else range.setStart(block, 0);
  range.collapse(true);
  root.focus({ preventScroll: true });
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  block.scrollIntoView?.({ block: "nearest" });
  return true;
};
